import { useMutation, useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { useDispatch } from "react-redux";
import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../../Graphql/utils";
import {
  createStudentQuery,
  getStudentQuery,
  graduateStudentMutation,
  listStudentsQuery,
  transferOutStudentMutation,
  updateStudentQuery,
  withdrawStudentMutation
} from "../../../Graphql/querries/education/studentQuery";
import {
  addStudent,
  setSelectedStudent,
  setStudentList,
  updateStudentInList
} from "../../../store/slices/studentSlice";
import {
  CreateStudentInput,
  CreateStudentResponse,
  GetStudentInput,
  GetStudentResponse,
  ListStudentsInput,
  ListStudentsResponse,
  Student,
  StudentExitInput,
  UpdateStudentInput,
  UpdateStudentResponse
} from "../../../components/Education/Students/types/student";
import { GraphQLData, unwrapGraphQLOperation } from "./educationGraphQLUtils";

export const useStudents = (input?: ListStudentsInput) => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useQuery({
    queryKey: [
      "students",
      input?.search ?? "",
      input?.page ?? 0,
      input?.size ?? 20
    ],
    queryFn: async (): Promise<Student[]> => {
      const data = await fetchGraphQL<
        GraphQLData<ListStudentsResponse, "listStudents">,
        { input: ListStudentsInput | Record<string, never> }
      >(listStudentsQuery, { input: input ?? {} }, setAlert);
      const response = unwrapGraphQLOperation(data, "listStudents");
      if (response.code !== API_RESPONSE_CODE.OK) {
        setAlert({
          code: ALERT_BANNER_CODE.GENERAL_ERROR,
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR
        });
        return [];
      }
      const students = response.students ?? [];
      dispatch(setStudentList(students));
      return students;
    }
  });
};

export const useStudent = (studentId?: string) => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useQuery({
    queryKey: ["student", studentId],
    enabled: Boolean(studentId),
    queryFn: async (): Promise<Student | null> => {
      const data = await fetchGraphQL<
        GraphQLData<GetStudentResponse, "getStudent">,
        { input: GetStudentInput }
      >(getStudentQuery, { input: { studentId: studentId ?? "" } }, setAlert);
      const response = unwrapGraphQLOperation(data, "getStudent");
      if (response.code !== API_RESPONSE_CODE.OK || !response.student) {
        setAlert({
          code: ALERT_BANNER_CODE.GENERAL_ERROR,
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR
        });
        return null;
      }
      dispatch(setSelectedStudent(response.student));
      return response.student;
    }
  });
};

export const useCreateStudent = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (input: CreateStudentInput) => {
      const data = await fetchGraphQL<
        GraphQLData<CreateStudentResponse, "createStudent">,
        { input: CreateStudentInput }
      >(createStudentQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "createStudent");
      if (response.code !== API_RESPONSE_CODE.OK || !response.student) {
        throw new Error(response.code);
      }
      return response.student;
    },
    onSuccess: (student) => {
      dispatch(addStudent(student));
      setAlert({
        code: ALERT_BANNER_CODE.SUCCESS,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const useUpdateStudent = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (input: UpdateStudentInput) => {
      const data = await fetchGraphQL<
        GraphQLData<UpdateStudentResponse, "updateStudent">,
        { input: UpdateStudentInput }
      >(updateStudentQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "updateStudent");
      if (response.code !== API_RESPONSE_CODE.OK || !response.student) {
        throw new Error(response.code);
      }
      return response.student;
    },
    onSuccess: (student) => {
      dispatch(updateStudentInList(student));
      setAlert({
        code: ALERT_BANNER_CODE.SUCCESS,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

const useStudentExitMutation = (
  operation: "withdrawStudent" | "graduateStudent" | "transferOutStudent",
  mutationDocument: string
) => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (input: StudentExitInput) => {
      const data = await fetchGraphQL<
        GraphQLData<UpdateStudentResponse, typeof operation>,
        { input: StudentExitInput }
      >(mutationDocument, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, operation);
      if (response.code !== API_RESPONSE_CODE.OK || !response.student) {
        throw new Error(response.code);
      }
      return response.student;
    },
    onSuccess: (student) => {
      dispatch(updateStudentInList(student));
      setAlert({
        code: ALERT_BANNER_CODE.SUCCESS,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const useWithdrawStudent = () =>
  useStudentExitMutation("withdrawStudent", withdrawStudentMutation);

export const useGraduateStudent = () =>
  useStudentExitMutation("graduateStudent", graduateStudentMutation);

export const useTransferOutStudent = () =>
  useStudentExitMutation("transferOutStudent", transferOutStudentMutation);
